import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { paletteLoaded, selectPalette } from "../store/slices/deviceSlice";
import { useFeatureFlagContext } from "./useFeatureFlagContext";
import {
  applyPalette,
  clearPalette,
  getStoredPalette,
  type Palette
} from "../utils/palette";

/**
 * Keeps the page's colour palette in step with this device's choice: reads it
 * once at start, then re-applies whenever it or the feature flag changes.
 * Classic only shows while `alphaClassicPalette` is on.
 */
export const usePaletteSync = (): void => {
  const dispatch = useAppDispatch();
  const palette = useAppSelector(selectPalette);
  const loadPalette = useCallback(
    (nextPalette: Palette) => dispatch(paletteLoaded(nextPalette)),
    [dispatch]
  );
  const { isFlagEnabled, featureFlagsLoaded } = useFeatureFlagContext();
  const [hasLoadedPalette, setHasLoadedPalette] = useState(false);
  const featureOn = isFlagEnabled("alphaClassicPalette");
  // Until both are read, leave whatever the pre-paint script already set alone.
  const ready = featureFlagsLoaded && hasLoadedPalette;

  useEffect(() => {
    let live = true;
    getStoredPalette().then((stored) => {
      if (live) {
        loadPalette(stored);
        setHasLoadedPalette(true);
      }
    });
    return () => {
      live = false;
    };
  }, [loadPalette]);

  useEffect(() => {
    if (!ready) return;
    // Classic only applies while the feature is on.
    if (featureOn) applyPalette(palette);
    else clearPalette();
  }, [palette, featureOn, ready]);
};
